// hooks/useStepNavigation.js
import { useState, useCallback } from "react";

/**
 * Manages step navigation logic for guided sessions.
 * Extracted from ARTSession for testability.
 *
 * @param {object} script - The script object with a `steps` array.
 * @param {function} onEnd - Called when the session should end.
 * @returns step navigation state and handlers.
 */
export function useStepNavigation(script, onEnd) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(true);
  const [showDotMovement, setShowDotMovement] = useState(false);
  const [activeCueText, setActiveCueText] = useState(null);

  const endSessionAndNotify = useCallback(() => {
    setShowDotMovement(false);
    setIsModalVisible(false);
    onEnd();
  }, [onEnd]);

  const captureCueText = useCallback(() => {
    if (!script) return;
    setActiveCueText(script.steps[currentStepIndex]?.cue_text || null);
  }, [script, currentStepIndex]);

  const handleOptionClick = useCallback(
    (nextStepIdOrOption) => {
      const option =
        typeof nextStepIdOrOption === "object" && nextStepIdOrOption !== null
          ? nextStepIdOrOption
          : null;
      const nextStepId =
        option !== null ? option.next_step_id : nextStepIdOrOption;

      if (nextStepId === null) {
        endSessionAndNotify();
        return;
      }

      if (!script) return;

      const nextStepIndex = script.steps.findIndex(
        (step) => step.id === nextStepId,
      );

      if (nextStepIndex === -1) {
        const isEndOption =
          nextStepId === null ||
          (option?.text &&
            /^\s*(finish|done|end\s*session)\s*$/i.test(option.text.trim()));
        if (isEndOption) {
          endSessionAndNotify();
        } else {
          console.error("Next step not found in the script.", { nextStepId, option });
        }
        return;
      }

      const isSameStep = nextStepIndex === currentStepIndex;
      const nextStep = script.steps[nextStepIndex];
      const isBranchingStep =
        nextStep?.options && nextStep.options.length > 1;
      const optionSaysNoDot = option?.run_dot_before_next === false;
      const showNextPromptOnly =
        !isSameStep && (isBranchingStep || optionSaysNoDot);

      setActiveCueText(script.steps[currentStepIndex]?.cue_text || null);
      if (!isSameStep) {
        setCurrentStepIndex(nextStepIndex);
      }
      setIsModalVisible(false);

      if (showNextPromptOnly) {
        setShowDotMovement(false);
        setIsModalVisible(true);
      } else {
        setShowDotMovement(true);
      }
    },
    [script, currentStepIndex, onEnd],
  );

  const handleNextClick = useCallback(() => {
    if (script && currentStepIndex < script.steps.length - 1) {
      setActiveCueText(script.steps[currentStepIndex]?.cue_text || null);
      setCurrentStepIndex(currentStepIndex + 1);
      setIsModalVisible(false);
      setShowDotMovement(true);
    } else {
      endSessionAndNotify();
    }
  }, [script, currentStepIndex, endSessionAndNotify]);

  const handleSkipStep = useCallback(() => {
    if (!script) return;
    setShowDotMovement(false);
    if (showDotMovement) {
      setIsModalVisible(true);
    } else if (currentStepIndex < script.steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
      setIsModalVisible(true);
    } else {
      endSessionAndNotify();
    }
  }, [script, currentStepIndex, showDotMovement, endSessionAndNotify]);

  const endDotMovement = useCallback(() => {
    setShowDotMovement(false);
    setActiveCueText(null);
    setIsModalVisible(true);
  }, []);

  return {
    currentStepIndex,
    setCurrentStepIndex,
    isModalVisible,
    setIsModalVisible,
    showDotMovement,
    setShowDotMovement,
    activeCueText,
    handleOptionClick,
    handleNextClick,
    handleSkipStep,
    endDotMovement,
    captureCueText,
  };
}
